"use client"

import type React from "react"
import { useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, Alert } from "reactstrap"
import { TicketService } from "../../../Service/TicketingService"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
}

const TicketVerificationModal: React.FC<Props> = ({ isOpen, toggle, onSuccess }) => {
  const [ticketNumber, setTicketNumber] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!ticketNumber.trim()) {
      setError("Please enter a ticket number")
      return
    }

    try {
      setLoading(true)
      setError("")

      const response = await TicketService.verify(ticketNumber.trim())

      if (response.success) {
        await Swal.fire({
          title: "Entry Allowed!",
          text: response.message,
          icon: "success",
          html: `
            <div class="text-start">
              <p><strong>Ticket:</strong> ${response.ticket_info.ticket_number}</p>
              <p><strong>Payment Ref:</strong> ${response.ticket_info.payment_reference}</p>
              <p><strong>Turnstile:</strong> ${response.ticket_info.turnstile}</p>
              <p><strong>Amount Paid:</strong> FRW ${response.ticket_info.amount_paid.toLocaleString()}</p>
              <p><strong>Phone:</strong> ${response.ticket_info.phone_number}</p>
            </div>
          `,
        })
        setTicketNumber("")
        onSuccess()
      }
    } catch (error: any) {
      console.error("Failed to verify ticket:", error)
      const errorMessage = error.response?.data?.message || "Verification failed"
      setError(errorMessage)

      if (error.response?.data?.used_at) {
        await Swal.fire({
          title: "Ticket Already Used",
          text: `This ticket was already used on ${new Date(error.response.data.used_at).toLocaleString()}`,
          icon: "warning",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setTicketNumber("")
    setError("")
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md">
      <ModalHeader toggle={handleClose}>Verify Ticket</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="ticketNumber">Ticket Number *</Label>
            <Input
              name="ticketNumber"
              value={ticketNumber}
              onChange={(e) => setTicketNumber(e.target.value)}
              placeholder="Enter 6-digit ticket number"
              maxLength={6}
              autoFocus
            />
          </FormGroup>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <div className="text-end">
            <Button color="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button color="success" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Verify Ticket"}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

export default TicketVerificationModal
